import { apiSSO } from '@rfacil/shared/services';
import axios from 'axios';
import { getErrorMessage } from '../hooks/errorHelpers';

/**
 * Buzón de notificaciones del SSO (M3-H1 y M3-H2).
 *
 * Todos los endpoints viven en `api/Buzon/*` del SSO y trabajan SIEMPRE sobre el buzón del usuario
 * del token: no hay forma de leer ni tocar el buzón de otro. Solo los roles Operativo y Empresa
 * reciben notificaciones (ver `canViewBuzon`). Las fechas llegan en UTC con `Z`, así que `new Date`
 * las interpreta bien. Contrato completo: `ClaudeContext/SSO_Rfacil.md` §16.
 */

/** Notificaciones por página en la pantalla del buzón. */
export const TAMANO_PAGINA_BUZON = 20;

/** Tope de página que acepta el SSO; fuera de rango responde 400. */
export const PAGINA_MAXIMA_BUZON = 10000;

/** Notificaciones por página que acepta el SSO como máximo; más que eso responde 400. */
export const TAMANO_MAXIMO_PAGINA_BUZON = 100;

/** Ids que acepta `MarcarLeidas` por llamada. */
export const MAXIMO_IDS_MARCAR_LEIDAS = 100;

/**
 * Vueltas máximas de `MarcarTodasLeidas` (1000 por llamada). Es un seguro contra un ciclo sin fin si
 * el SSO siguiera reportando `Restantes`; en la práctica basta una llamada.
 */
const VUELTAS_MAXIMAS_MARCAR_TODAS = 20;

/**
 * Estado de una notificación. `Pendiente` = sigue en el buzón; `Atendida` = salió porque se atendió
 * la acción que la originó; `Descartada` = el usuario quitó el aviso. El SSO compara el texto exacto.
 */
export type EstadoNotificacionBuzon = 'Pendiente' | 'Atendida' | 'Descartada';

/** Filtro de estado de la lista: los tres estados o `Todas`. */
export type FiltroEstadoBuzon = EstadoNotificacionBuzon | 'Todas';

/** Respuesta de `GET api/Buzon/Contador`: lo mínimo para la campanita y el aviso al iniciar sesión. */
export interface ContadorBuzonDTO {
  /** Todo lo que sigue en el buzón: pendientes accionables y avisos sin descartar. */
  Pendientes: number;
  /** De los anteriores, los que no se han leído. */
  NoLeidas: number;
  /**
   * De los pendientes, los que requieren acción (`Accionable = true`), con el mismo alcance que
   * `Pendientes`. Campo nuevo del SSO: mientras no llegue, la campanita no muestra el punto ámbar.
   */
  Accionables?: number;
}

/** Una categoría del buzón con sus contadores (solo de lo que sigue en el buzón). */
export interface CategoriaBuzonDTO {
  Clave: string;
  Nombre: string;
  Orden: number;
  Pendientes: number;
  NoLeidas: number;
}

/**
 * Respuesta de `GET api/Buzon/Resumen`: todas las categorías activas, en su orden y aunque estén en
 * cero. El catálogo vive en el SSO, así que el front no tiene una lista fija de categorías.
 */
export interface ResumenBuzonDTO {
  TotalPendientes: number;
  TotalNoLeidas: number;
  Categorias: CategoriaBuzonDTO[];
}

/** Una notificación del buzón tal como la manda `GET api/Buzon/Notificaciones`. */
export interface NotificacionBuzonDTO {
  Id: number;
  ClaveCategoria: string;
  ClaveTipoEvento: string;
  NombreTipoEvento: string;
  /** Módulo dueño del registro (`Materialidad`, `EFirma`, ...); con `TipoEntidad` arma el enlace. */
  Modulo: string;
  /** `true` = pendiente que sale solo al atender la acción (no se descarta); `false` = aviso. */
  Accionable: boolean;
  IdEmpresa: number;
  Titulo: string;
  Mensaje: string;
  TipoEntidad: string;
  /** Texto: no siempre es numérico. */
  IdEntidad: string;
  /** JSON libre de quien registró la notificación; `null` si no vino. */
  Datos: unknown;
  Estado: EstadoNotificacionBuzon;
  Leida: boolean;
  FechaCreacion: string;
  FechaLectura: string | null;
  FechaAtencion: string | null;
  /** Quién la originó si fue un usuario; `null` si fue un sistema. */
  IdUsuarioOrigen: number | null;
  /** Nombre de usuario de quien la originó; `null` si fue un sistema. */
  UsuarioOrigen: string | null;
  /** Cliente de sistema que la originó (por ejemplo `SSO`); `null` si fue un usuario. */
  SistemaOrigen: string | null;
  /** Motivo del cambio si quien la creó lo mandó; normalmente `null`. */
  Motivo: string | null;
}

/** Una página de `GET api/Buzon/Notificaciones`. */
export interface PaginaNotificacionesBuzonDTO {
  Pagina: number;
  Tamano: number;
  Total: number;
  Elementos: NotificacionBuzonDTO[];
}

/** Filtro de la lista del buzón. Sin categoría = todas; sin estado = `Pendiente`. */
export interface FiltroNotificacionesBuzon {
  categoria?: string | null;
  estado?: FiltroEstadoBuzon;
  pagina?: number;
  tamano?: number;
}

/** Un evento notificado sobre un registro (`GET api/Buzon/Historial`), un renglón por evento. */
export interface EventoHistorialBuzonDTO {
  IdLote: string;
  ClaveTipoEvento: string;
  NombreTipoEvento: string;
  Titulo: string;
  Mensaje: string;
  Motivo: string | null;
  FechaCreacion: string;
  IdUsuarioOrigen: number | null;
  UsuarioOrigen: string | null;
  SistemaOrigen: string | null;
  /** A cuántos usuarios les llegó. */
  Destinatarios: number;
}

/** Preferencia de correo del propio usuario para un tipo de evento. */
export interface PreferenciaCorreoBuzonDTO {
  ClaveTipoEvento: string;
  NombreTipoEvento: string;
  /** Qué dispara el evento, en palabras del usuario; `null` si el tipo no la tiene. */
  Descripcion: string | null;
  ClaveCategoria: string;
  NombreCategoria: string;
  RecibirCorreo: boolean;
}

/** Un cambio de preferencia para `PUT api/Buzon/Preferencias`. */
export interface CambioPreferenciaCorreo {
  ClaveTipoEvento: string;
  RecibirCorreo: boolean;
}

/**
 * Ejecuta una llamada al SSO y convierte cualquier falla en un `Error` con el mensaje del backend
 * (`ApiResponse.Message`), para que la pantalla nunca muestre `[object Object]`.
 */
async function llamarBuzon<T>(operacion: string, llamada: () => Promise<T>): Promise<T> {
  try {
    return await llamada();
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(`Error de API en ${operacion}: ${error.response?.status}`, error);
    } else {
      console.error(`Error en ${operacion}:`, error);
    }
    throw new Error(getErrorMessage(error));
  }
}

/** Arreglo tal cual o vacío: el SSO responde 200 y la UI nunca debe recibir algo que no pueda recorrer. */
const comoLista = <T>(data: unknown): T[] => (Array.isArray(data) ? (data as T[]) : []);

/**
 * @function getContadorBuzon
 * @description Pendientes y no leídas del usuario, para la campanita y el aviso al iniciar sesión.
 * `Accionables` solo se incluye si el SSO lo manda como número.
 */
export function getContadorBuzon(): Promise<ContadorBuzonDTO> {
  return llamarBuzon('getContadorBuzon', async () => {
    const { data } = await apiSSO.get<ContadorBuzonDTO>('/Buzon/Contador');
    const contador: ContadorBuzonDTO = { Pendientes: data?.Pendientes ?? 0, NoLeidas: data?.NoLeidas ?? 0 };
    if (typeof data?.Accionables === 'number') {
      contador.Accionables = data.Accionables;
    }
    return contador;
  });
}

/**
 * @function getResumenBuzon
 * @description Categorías activas con sus contadores, en el orden del catálogo.
 */
export function getResumenBuzon(): Promise<ResumenBuzonDTO> {
  return llamarBuzon('getResumenBuzon', async () => {
    const { data } = await apiSSO.get<ResumenBuzonDTO>('/Buzon/Resumen');
    return {
      TotalPendientes: data?.TotalPendientes ?? 0,
      TotalNoLeidas: data?.TotalNoLeidas ?? 0,
      Categorias: comoLista<CategoriaBuzonDTO>(data?.Categorias),
    };
  });
}

/**
 * @function getNotificacionesBuzon
 * @description Una página de notificaciones del usuario, más recientes primero. Página y tamaño se
 * acotan a lo que acepta el SSO para no provocar un 400 desde la propia pantalla.
 */
export function getNotificacionesBuzon(
  filtro: FiltroNotificacionesBuzon = {}
): Promise<PaginaNotificacionesBuzonDTO> {
  const pagina = Math.min(Math.max(1, Math.trunc(filtro.pagina ?? 1)), PAGINA_MAXIMA_BUZON);
  const tamano = Math.min(Math.max(1, Math.trunc(filtro.tamano ?? TAMANO_PAGINA_BUZON)), TAMANO_MAXIMO_PAGINA_BUZON);
  const params: Record<string, string | number> = {
    Estado: filtro.estado ?? 'Pendiente',
    Pagina: pagina,
    Tamano: tamano,
  };
  if (filtro.categoria) {
    params.Categoria = filtro.categoria;
  }

  return llamarBuzon('getNotificacionesBuzon', async () => {
    const { data } = await apiSSO.get<PaginaNotificacionesBuzonDTO>('/Buzon/Notificaciones', { params });
    return {
      Pagina: data?.Pagina ?? pagina,
      Tamano: data?.Tamano ?? tamano,
      Total: data?.Total ?? 0,
      Elementos: comoLista<NotificacionBuzonDTO>(data?.Elementos),
    };
  });
}

/**
 * @function marcarNotificacionesLeidas
 * @description Marca como leídas notificaciones propias. El SSO acepta hasta 100 Ids por llamada,
 * así que una lista más larga se manda en bloques. Regresa cuántas cambiaron.
 */
export function marcarNotificacionesLeidas(ids: number[]): Promise<number> {
  const unicos = Array.from(new Set(ids));
  if (unicos.length === 0) {
    return Promise.resolve(0);
  }

  return llamarBuzon('marcarNotificacionesLeidas', async () => {
    let marcadas = 0;
    for (let i = 0; i < unicos.length; i += MAXIMO_IDS_MARCAR_LEIDAS) {
      const bloque = unicos.slice(i, i + MAXIMO_IDS_MARCAR_LEIDAS);
      const { data } = await apiSSO.post<{ Marcadas: number }>('/Buzon/MarcarLeidas', { Ids: bloque });
      marcadas += data?.Marcadas ?? 0;
    }
    return marcadas;
  });
}

/**
 * @function marcarTodasLeidas
 * @description "Marcar todas como leídas", opcionalmente solo de una categoría. El SSO marca hasta
 * 1000 por llamada y responde `Restantes`: se repite mientras queden. Regresa cuántas cambiaron.
 */
export function marcarTodasLeidas(categoria?: string | null): Promise<number> {
  const body = categoria ? { Categoria: categoria } : {};

  return llamarBuzon('marcarTodasLeidas', async () => {
    let marcadas = 0;
    for (let vuelta = 0; vuelta < VUELTAS_MAXIMAS_MARCAR_TODAS; vuelta++) {
      const { data } = await apiSSO.post<{ Marcadas: number; Restantes: number }>(
        '/Buzon/MarcarTodasLeidas',
        body
      );
      marcadas += data?.Marcadas ?? 0;
      // Sin avance no se insiste: evita un ciclo si el SSO reportara restantes que no puede marcar.
      if (!data || data.Restantes <= 0 || data.Marcadas <= 0) {
        break;
      }
    }
    return marcadas;
  });
}

/**
 * @function descartarNotificacion
 * @description Quita del buzón un aviso propio (`Accionable: false`). El SSO responde 400 si es un
 * pendiente accionable y 404 si no es del usuario; el mensaje del backend llega en el `Error`.
 */
export function descartarNotificacion(idNotificacion: number): Promise<void> {
  return llamarBuzon('descartarNotificacion', async () => {
    await apiSSO.post(`/Buzon/Descartar/${idNotificacion}`);
  });
}

/**
 * @function getHistorialBuzon
 * @description Lo notificado sobre un registro (por ejemplo una factura), con su motivo, más
 * recientes primero. Un Operativo solo recibe lo que le llegó o lo que él originó.
 */
export function getHistorialBuzon(
  idEmpresa: number,
  tipoEntidad: string,
  idEntidad: string
): Promise<EventoHistorialBuzonDTO[]> {
  return llamarBuzon('getHistorialBuzon', async () => {
    const { data } = await apiSSO.get('/Buzon/Historial', {
      params: { idEmpresa, tipoEntidad, idEntidad },
    });
    return comoLista<EventoHistorialBuzonDTO>(data);
  });
}

/**
 * @function getPreferenciasCorreo
 * @description Tipos de evento que admiten correo, con la preferencia del usuario (apagada por defecto).
 */
export function getPreferenciasCorreo(): Promise<PreferenciaCorreoBuzonDTO[]> {
  return llamarBuzon('getPreferenciasCorreo', async () => {
    const { data } = await apiSSO.get('/Buzon/Preferencias');
    return comoLista<PreferenciaCorreoBuzonDTO>(data);
  });
}

/**
 * @function guardarPreferenciasCorreo
 * @description Guarda solo lo que cambió (de 1 a 50 tipos) y regresa la lista completa actualizada.
 */
export function guardarPreferenciasCorreo(
  cambios: CambioPreferenciaCorreo[]
): Promise<PreferenciaCorreoBuzonDTO[]> {
  return llamarBuzon('guardarPreferenciasCorreo', async () => {
    const { data } = await apiSSO.put('/Buzon/Preferencias', { Preferencias: cambios });
    return comoLista<PreferenciaCorreoBuzonDTO>(data);
  });
}
